import React, { useState, useCallback, useEffect } from 'react';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { Button, Stack, Typography, Tooltip, CircularProgress } from '@mui/material';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useRouter } from '@/hooks';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import { VIEW_IN_3D_TOOLTIP_LABEL } from '@/constants';
import {
  AttachmentActionContainer,
  AttachmentContainer,
  ImageCaption,
  imageStyle,
  textStyle,
} from './styles';
import { Attachment } from '@/components/shared/AttachmentDisplay/type';
import UploadFileDialog from '@/components/Analysis/shared/MediaUploadsAndViewer/UploadFileDialog';
import { urlToBase64 } from '@/utils';
import {
  FileTypes,
  FileUploadStatus,
  useGetAssemblyAttachmentsQuery,
} from '@/__generated__/graphql';

type ActionButtonsProps = {
  isUpdating?: boolean;
  onView3D: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onUploadImages: () => void;
};

const ActionButtons = ({ isUpdating, onView3D, onUploadImages }: ActionButtonsProps) => (
  <Stack direction="row" gap={2}>
    <Tooltip title={VIEW_IN_3D_TOOLTIP_LABEL} placement="bottom" arrow>
      <Button
        variant="outlined"
        size="small"
        startIcon={<AbyssIcon iconName="cube-secondary" size="md" />}
        onClick={onView3D}
      >
        Add 3D View
      </Button>
    </Tooltip>
    <Button
      variant="outlined"
      size="small"
      startIcon={
        isUpdating ? <CircularProgress size={12} /> : <AbyssIcon iconName="media" size="md" />
      }
      onClick={onUploadImages}
    >
      {isUpdating ? 'Uploading' : 'Upload Images'}
    </Button>
  </Stack>
);

const InfoPanel = ({ text, textSize }: { text: string; textSize: string }) => (
  <>
    <AbyssIcon iconName="attach-media" size="2xl" />
    <Typography sx={{ ...textStyle, fontSize: textSize }}>{text}</Typography>
  </>
);

const { Png, Jpeg } = FileTypes;

export const AttachmentsSection = () => {
  const router = useRouter();
  const structureId = useGetStructureId();
  const selectedAssemblyName = useAtomValue(state.selectedAssemblyName);
  const selectedAssemblyId = useAtomValue(state.selectedAssemblyId);
  const [openFileDialog, setOpenFileDialog] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  const { data } = useGetAssemblyAttachmentsQuery({
    variables: { assemblyId: selectedAssemblyId },
  });

  useEffect(() => {
    const mapAssemblyAttachments = async () => {
      const files = data?.assembly?.files || [];
      const imageFiles = files.filter(
        ({ type, id, uploadStatus }) =>
          [Png, Jpeg].includes(type) && id && uploadStatus === FileUploadStatus.Success
      );
      const mappedAttachments = await Promise.all(
        imageFiles.map(async (file) => {
          let imageBase64 = '';
          if (file.resourcePath) {
            imageBase64 = await urlToBase64(getCloudfrontUrl(file.resourcePath));
          }
          return {
            id: file.id!,
            name: file.name,
            type: file.type,
            url: getCloudfrontUrl(file.resourcePath),
            caption: file.caption ?? undefined,
            isNew: false,
            resourcePath: file.resourcePath,
            base64: imageBase64,
          };
        })
      );
      setAttachments(mappedAttachments);
    };
    if (data) mapAssemblyAttachments();
  }, [data]);

  const handleViewIn3D = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const url = `/analysis/${structureId}/viewer?assembly=${selectedAssemblyName}`;
      const isCtrlClick = event.ctrlKey || event.metaKey;

      isCtrlClick ? window.open(url, '_blank') : router.push(url);
    },
    [router, structureId, selectedAssemblyName]
  );

  const handleUploadImages = useCallback(() => {
    setOpenFileDialog(true);
  }, []);

  return (
    <AttachmentContainer gap={2}>
      {attachments?.length > 0 ? (
        <Stack direction="column" alignItems="center" p={2} sx={{ width: '100%' }}>
          {attachments?.map((img) => (
            <React.Fragment key={img.id}>
              <img src={img?.url} alt={img?.name} style={imageStyle} />
              <ImageCaption>{img?.caption ?? img?.name}</ImageCaption>
            </React.Fragment>
          ))}
          <AttachmentActionContainer>
            <Stack direction="row" alignItems="center" gap={2}>
              <InfoPanel
                textSize="1.2rem"
                text="You can add more images from different spherical or upload isometric diagram here"
              />
            </Stack>
            <ActionButtons
              isUpdating={isUpdating}
              onView3D={handleViewIn3D}
              onUploadImages={handleUploadImages}
            />
          </AttachmentActionContainer>
        </Stack>
      ) : (
        <>
          <InfoPanel
            textSize="1.2rem"
            text="You can either take a screenshot from the 3D view or upload an image here with the POI locations"
          />
          <ActionButtons
            isUpdating={isUpdating}
            onView3D={handleViewIn3D}
            onUploadImages={handleUploadImages}
          />
        </>
      )}
      <UploadFileDialog
        openDialog={openFileDialog}
        setOpenDialog={setOpenFileDialog}
        referenceId={selectedAssemblyId ?? ''}
        collectionName="assembly"
        attachments={attachments}
        isComponentOverview={true}
        setAttachments={setAttachments}
        setIsUpdating={setIsUpdating}
        dialogTitle="Attachments"
        buttonText="Add"
      />
    </AttachmentContainer>
  );
};
